// WhaleActivity Component - Professional visualization of on-chain whale movements
import React, { useState, useEffect } from 'react';
import {
    Activity,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    Wifi,
    Users,
    BarChart3,
    ExternalLink,
    RefreshCw,
    Zap,
    Globe,
    Database
} from 'lucide-react';
import { useRecentSignals } from '../../hooks/useDuckSignals';
import { formatLargeNumber } from '../../services/tokenService';

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const THEME = {
    purple: '#836EF9',
    yellow: '#FFD700',
    orange: '#FF8C00',
    green: '#22c55e',
    red: '#ef4444',
    // Vibrant cards on purple background
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(255, 215, 0, 0.2)',
};

const styles = {
    card: {
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: '20px',
        overflow: 'hidden',
    } as React.CSSProperties,
    cardHeader: {
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    } as React.CSSProperties,
    cardBody: {
        padding: '1.5rem',
    } as React.CSSProperties,
    statCard: (color: string) => ({
        background: `${color}10`,
        border: `1px solid ${color}25`,
        borderRadius: '16px',
        padding: '1.25rem',
        textAlign: 'center' as const,
    }),
};

// Types
interface WhaleAlert {
    id: string;
    type: 'ACCUMULATION' | 'DISTRIBUTION' | 'MEGA_ACCUMULATION' | 'MEGA_DISTRIBUTION';
    wallet: string;
    balanceChange: number;
    percentChange: number;
    newBalance: number;
    percentOfSupply: number;
    impact: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
    sentiment: 'BULLISH' | 'BEARISH' | 'NEUTRAL' | 'VERY_BULLISH' | 'VERY_BEARISH';
    timestamp: number;
}

interface NetworkStats {
    blockHeight: number;
    gasPrice: number;
    txPerBlock: number;
    tx24h: number;
    congestion: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
    healthScore: number;
}

// Parse whale alert from signal reason
function parseWhaleAlert(reason: string, timestamp: number): WhaleAlert | null {
    if (!reason.includes('🐋')) return null;

    try {
        const parts = reason.split('|').map(p => p.trim());
        const typeMatch = parts[0]?.match(/🐋\s*(\w+)/);
        const type = (typeMatch?.[1] || 'ACCUMULATION') as WhaleAlert['type'];

        const walletMatch = parts.find(p => p.includes('Wallet:'));
        const wallet = walletMatch?.replace('Wallet:', '').trim() || '0x...';

        const changeMatch = parts.find(p => p.includes('Change:'));
        const changeStr = changeMatch?.replace('Change:', '').trim() || '0';
        const balanceChange = parseFloat(changeStr.replace(/[+,]/g, '').split(' ')[0]) || 0;
        const percentMatch = changeStr.match(/\((.*?)%\)/);
        const percentChange = parseFloat(percentMatch?.[1] || '0');

        const holdingsMatch = parts.find(p => p.includes('Holdings:'));
        const holdingsStr = holdingsMatch?.replace('Holdings:', '').trim() || '0';
        const newBalance = parseFloat(holdingsStr.replace(/[,]/g, '').split(' ')[0]) || 0;
        const supplyMatch = holdingsStr.match(/\((.*?)%/);
        const percentOfSupply = parseFloat(supplyMatch?.[1] || '0');

        const impactMatch = parts.find(p => p.includes('Impact:'));
        const impactStr = impactMatch?.split('|')[0]?.replace('Impact:', '').trim() || 'LOW';
        const impact = impactStr as WhaleAlert['impact'];

        const sentimentMatch = parts.find(p => p.includes('Impact:'))?.split('|')[1]?.trim();
        const sentiment = (sentimentMatch || 'NEUTRAL') as WhaleAlert['sentiment'];

        return { id: `${wallet}-${timestamp}`, type, wallet, balanceChange, percentChange, newBalance, percentOfSupply, impact, sentiment, timestamp };
    } catch {
        return null;
    }
}

// Parse network stats
function parseNetworkStats(reason: string): Partial<NetworkStats> | null {
    if (!reason.includes('📡')) return null;

    try {
        const parts = reason.split('|').map(p => p.trim());
        const blockMatch = parts.find(p => p.includes('Block:'));
        const blockHeight = parseInt(blockMatch?.replace(/[^0-9]/g, '') || '0');
        const gasMatch = parts.find(p => p.includes('Gas:'));
        const gasPrice = parseFloat(gasMatch?.replace(/[^0-9.]/g, '') || '0');
        const txMatch = parts.find(p => p.includes('TX/Block:'));
        const txPerBlock = parseInt(txMatch?.replace(/[^0-9]/g, '') || '0');
        const congestionMatch = parts.find(p => p.includes('Congestion:'));
        const congestion = (congestionMatch?.replace('Congestion:', '').trim() || 'LOW') as NetworkStats['congestion'];
        const healthMatch = parts.find(p => p.includes('Health:'));
        const healthScore = parseInt(healthMatch?.replace(/[^0-9]/g, '') || '100');

        return { blockHeight, gasPrice, txPerBlock, congestion, healthScore };
    } catch {
        return null;
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// WHALE ALERT CARD
// ═══════════════════════════════════════════════════════════════════════════════

const WhaleAlertCard: React.FC<{ alert: WhaleAlert }> = ({ alert }) => {
    const isAccumulation = alert.type.includes('ACCUMULATION');
    const color = isAccumulation ? THEME.green : THEME.red;

    const impactColors: Record<string, string> = {
        CRITICAL: THEME.red,
        HIGH: THEME.orange,
        MODERATE: THEME.yellow,
        LOW: THEME.green,
    };

    return (
        <div style={{
            background: `linear-gradient(135deg, ${color}08, rgba(0,0,0,0.3))`,
            border: `1px solid ${color}25`,
            borderRadius: '16px',
            padding: '1.25rem',
            marginBottom: '1rem',
        }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                        width: '48px', height: '48px', borderRadius: '12px',
                        background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1.5rem',
                    }}>🐋</div>
                    <div>
                        <div style={{ fontWeight: 700, color: '#fff', marginBottom: '0.25rem' }}>
                            {alert.type.replace('_', ' ')}
                        </div>
                        <a href={`https://monadvision.com/address/${alert.wallet}`} target="_blank" rel="noopener noreferrer"
                            style={{ color: THEME.purple, fontSize: '0.75rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {alert.wallet} <ExternalLink size={10} />
                        </a>
                    </div>
                </div>
                <span style={{
                    padding: '0.35rem 0.75rem', borderRadius: '9999px', fontSize: '0.65rem', fontWeight: 700,
                    background: `${impactColors[alert.impact]}20`, color: impactColors[alert.impact],
                    textTransform: 'uppercase',
                }}>{alert.impact}</span>
            </div>

            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ background: 'rgba(0,0,0,0.4)', padding: '0.75rem', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase' }}>Change</div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: alert.balanceChange >= 0 ? THEME.green : THEME.red }}>
                        {alert.balanceChange >= 0 ? '+' : ''}{formatLargeNumber(alert.balanceChange)}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                        ({alert.percentChange >= 0 ? '+' : ''}{alert.percentChange.toFixed(2)}%)
                    </div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.4)', padding: '0.75rem', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase' }}>Holdings</div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>{formatLargeNumber(alert.newBalance)}</div>
                    <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>DUCK</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.4)', padding: '0.75rem', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.25rem', textTransform: 'uppercase' }}>% Supply</div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: THEME.purple }}>{alert.percentOfSupply.toFixed(2)}%</div>
                </div>
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: alert.sentiment.includes('BULLISH') ? THEME.green : alert.sentiment.includes('BEARISH') ? THEME.red : '#9ca3af' }}>
                    {alert.sentiment.includes('BULLISH') ? <TrendingUp size={16} /> : alert.sentiment.includes('BEARISH') ? <TrendingDown size={16} /> : <Activity size={16} />}
                    <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{alert.sentiment.replace('_', ' ')}</span>
                </div>
                <span style={{ color: '#6b7280', fontSize: '0.7rem' }}>
                    {new Date(alert.timestamp * 1000).toLocaleTimeString()}
                </span>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// NETWORK HEALTH CARD
// ═══════════════════════════════════════════════════════════════════════════════

const NetworkHealthCard: React.FC<{ stats: NetworkStats }> = ({ stats }) => {
    const congestionColors: Record<string, string> = { LOW: THEME.green, MODERATE: THEME.yellow, HIGH: THEME.orange, CRITICAL: THEME.red };

    return (
        <div style={styles.card}>
            <div style={styles.cardHeader}>
                <Globe size={18} color={THEME.purple} />
                <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.875rem' }}>Monad Network</span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', color: congestionColors[stats.congestion] }}>
                    <Wifi size={14} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{stats.congestion}</span>
                </div>
            </div>
            <div style={styles.cardBody}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                    <div style={{ background: 'rgba(0,0,0,0.4)', padding: '1rem', borderRadius: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <Database size={14} color="#6b7280" />
                            <span style={{ color: '#6b7280', fontSize: '0.7rem', textTransform: 'uppercase' }}>Block</span>
                        </div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.125rem' }}>{stats.blockHeight.toLocaleString()}</div>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.4)', padding: '1rem', borderRadius: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <Zap size={14} color="#6b7280" />
                            <span style={{ color: '#6b7280', fontSize: '0.7rem', textTransform: 'uppercase' }}>Gas</span>
                        </div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.125rem' }}>{stats.gasPrice.toFixed(2)} <span style={{ color: '#6b7280', fontWeight: 400, fontSize: '0.75rem' }}>gwei</span></div>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.4)', padding: '1rem', borderRadius: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <BarChart3 size={14} color="#6b7280" />
                            <span style={{ color: '#6b7280', fontSize: '0.7rem', textTransform: 'uppercase' }}>TX/Block</span>
                        </div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.125rem' }}>{stats.txPerBlock.toLocaleString()}</div>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.4)', padding: '1rem', borderRadius: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <Activity size={14} color="#6b7280" />
                            <span style={{ color: '#6b7280', fontSize: '0.7rem', textTransform: 'uppercase' }}>Health</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ flex: 1, height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '9999px', overflow: 'hidden' }}>
                                <div style={{
                                    width: `${stats.healthScore}%`, height: '100%', borderRadius: '9999px',
                                    background: stats.healthScore > 80 ? THEME.green : stats.healthScore > 50 ? THEME.yellow : THEME.red,
                                }} />
                            </div>
                            <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.875rem' }}>{stats.healthScore}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════


// Main Component
const WhaleActivity: React.FC<{ selectedToken?: any; whaleAlerts?: any[] }> = ({ selectedToken, whaleAlerts: _externalAlerts }) => { // Accepted token prop
    const { signals, isLoading } = useRecentSignals(50);
    const [whaleAlerts, setWhaleAlerts] = useState<WhaleAlert[]>([]);
    const [networkStats, setNetworkStats] = useState<NetworkStats>({
        blockHeight: 0, gasPrice: 0, txPerBlock: 0, tx24h: 0, congestion: 'LOW', healthScore: 100,
    });

    useEffect(() => {
        if (!signals || signals.length === 0) return;

        const alerts: WhaleAlert[] = [];
        let latestNetwork: Partial<NetworkStats> | null = null;

        // Filter signals if a token is selected and we can identify it (not possible with current contract data, so we show all for now)
        // Ideally, we would filter signals here.

        for (const signal of signals) {
            const reason = signal.reason || '';
            const timestamp = Number(signal.timestamp);

            const alert = parseWhaleAlert(reason, timestamp);
            if (alert) alerts.push(alert);

            const network = parseNetworkStats(reason);
            if (network && !latestNetwork) latestNetwork = network;
        }

        // If no on-chain alerts found, we simply show nothing or "No Activity"
        // User requested NO demo data.

        setWhaleAlerts(alerts);
        if (latestNetwork) setNetworkStats(prev => ({ ...prev, ...latestNetwork }));
    }, [signals, selectedToken]);

    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#9ca3af' }}>
                <RefreshCw size={24} style={{ marginRight: '0.75rem', animation: 'spin 1s linear infinite' }} />
                Loading whale activity...
            </div>
        );
    }

    const accumulations = whaleAlerts.filter(a => a.type.includes('ACCUMULATION')).length;
    const distributions = whaleAlerts.filter(a => a.type.includes('DISTRIBUTION')).length;
    const highImpact = whaleAlerts.filter(a => a.impact === 'CRITICAL' || a.impact === 'HIGH').length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        🐋 Whale Activity
                        {selectedToken && <span style={{ fontSize: '0.9rem', color: THEME.purple, background: `${THEME.purple}20`, padding: '0.1rem 0.5rem', borderRadius: '8px' }}>{selectedToken.symbol}</span>}
                    </h2>
                    <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
                        Real-time whale movements and network intelligence
                    </p>
                </div>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    background: `${THEME.green}20`, color: THEME.green,
                    padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700,
                }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: THEME.green, animation: 'pulse 2s infinite' }} />
                    LIVE
                </div>
            </div>

            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                <div style={styles.statCard(THEME.green)}>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: THEME.green }}>{accumulations}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Accumulations</div>
                </div>
                <div style={styles.statCard(THEME.red)}>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: THEME.red }}>{distributions}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Distributions</div>
                </div>
                <div style={styles.statCard(THEME.orange)}>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: THEME.orange }}>{highImpact}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>High Impact</div>
                </div>
                <div style={styles.statCard(THEME.purple)}>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: THEME.purple }}>{whaleAlerts.length}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Alerts</div>
                </div>
            </div>

            {/* Two Column Layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.5rem' }}>
                {/* Whale Alerts */}
                <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Users size={16} /> Recent Whale Movements
                    </h3>

                    {whaleAlerts.length > 0 ? (
                        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {whaleAlerts.map(alert => <WhaleAlertCard key={alert.id} alert={alert} />)}
                        </div>
                    ) : (
                        <div style={{ ...styles.card, padding: '3rem', textAlign: 'center' }}>
                            <AlertTriangle size={40} color="#6b7280" style={{ marginBottom: '1rem' }} />
                            <p style={{ color: '#9ca3af', margin: 0 }}>No whale activity detected for {selectedToken?.symbol || 'Monad'}.</p>
                            <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
                                Whale Observer monitors large wallet movements.
                            </p>
                        </div>
                    )}
                </div>

                {/* Right Panel */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <NetworkHealthCard stats={networkStats} />

                    {/* Agent Info */}
                    <div style={{ ...styles.card, padding: '1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: THEME.green }} />
                            <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>Whale Observer v1.0</span>
                        </div>
                        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0, lineHeight: 1.6 }}>
                            Monitoring top {selectedToken?.symbol || 'DUCK'} holders for accumulation and distribution patterns.
                            Alerts posted on-chain to DuckSignals contract.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WhaleActivity;
